import { iconWhiteTrash } from "assets/icons";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { CheckBox } from "components/CheckBox";
import { InputFormField } from "components/InputFormField";
import { InputImage } from "components/InputImage";
import { InputMultipleFiles } from "components/InputMultipleFiles";
import { RichTextEditor } from "components/RichTextEditor";
import { SelectModified } from "components/SelectModified";
import { useAlert } from "hooks/useAlert";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  ImageDetails,
  ImagePreview,
} from "../../Sellers/SellerDetails/Identity/styles";
import { useExhibitorForm } from "../EventDetails/ExhibitorsTable/useExhibitorForm";
import { Container, FormActions, FormSection, Wrapper } from "./styles";

interface ExhibitorFormProps {
  eventId?: string;
  exhibitorId?: string;
}

const sponsorTierOptions = [
  { value: "", label: "Selecione..." },
  { value: "Diamond", label: "Diamante" },
  { value: "Platinum", label: "Platina" },
  { value: "Gold", label: "Ouro" },
  { value: "Silver", label: "Prata" },
  { value: "Bronze", label: "Bronze" },
  { value: "Support", label: "Apoio" },
];

export const ExhibitorForm: React.FC<ExhibitorFormProps> = ({
  eventId,
  exhibitorId,
}) => {
  const { push } = useHistory();
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const { setTextAlertInterface, openAlert, closeAlert, alert } = useAlert();
  const [editingExhibitor, setEditingExhibitor] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const onSuccess = () => {
    push(`/events/${eventId}?tab=Expositores`);
  };

  const {
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    inputsValues,
    touched,
    errors,
    handleSubmit,
    isLoading,
    handleImageChange,
    imagePath,
    logoIsPNG,
    isEditing,
    uploadedFiles,
    handleAddFiles,
    handleRemoveFile,
  } = useExhibitorForm(onSuccess, editingExhibitor, eventId);

  const getErrorMessage = (error: any): string | undefined => {
    if (typeof error === "string") return error;
    if (Array.isArray(error)) return error[0];
    return undefined;
  };

  // Load exhibitor data for editing
  useEffect(() => {
    if (exhibitorId) {
      loadExhibitorData();
    }
  }, [exhibitorId]);

  // Handle alert confirmation for delete
  useEffect(() => {
    if (alert.typeContinue === "save") {
      executeDeleteExhibitor();
    }
  }, [alert]);

  const loadExhibitorData = async () => {
    if (!exhibitorId) return;

    setLoading(true);

    try {
      const query = `
        query BackofficeExhibitor($exhibitorId: ID!) {
          backofficeExhibitor(exhibitorId: $exhibitorId) {
            exhibitorId
            eventId
            name
            description
            logo {
              fileId
              filename
              fileUrl
              signedFileUrl
              mimeType
            }
            websiteUrl
            boothNumber
            sponsorTier
            isSponsor
            contactEmail
            contactPhone
            displayPosition
            insertDate
            updateDate
            files {
              fileId
              filename
              fileUrl
              signedFileUrl
              mimeType
            }
          }
        }
      `;

      const response = await connectGraphQLApi({
        query,
        variables: { exhibitorId },
        errorMessage: "Falha ao carregar detalhes do expositor",
        useNewApi: true,
      });

      const exhibitorDetails = response.data.backofficeExhibitor;
      console.log(`exhibitorDetails files`, exhibitorDetails?.files);
      setEditingExhibitor(exhibitorDetails);
    } catch (error) {
      console.error("Error loading exhibitor details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExhibitor = () => {
    if (!exhibitorId || !editingExhibitor) return;

    setTextAlertInterface({
      title: "Excluir expositor?",
      description: `Ao continuar, o expositor "${editingExhibitor.name}" e todas as suas informações serão removidas.\nAtenção! Essa ação não pode ser revertida.`,
      closeAlertText: "Cancelar",
      continueAlertText: "Excluir",
      closeAlertTypeBtn: "outline",
      continueAlertTypeBtn: "danger",
      positionCenter: true,
      actionNeeded: false,
    });

    openAlert(true, "deleteExhibitor");
  };

  const executeDeleteExhibitor = async () => {
    if (!exhibitorId) return;

    try {
      const response = await connectGraphQLApi({
        query: `
                    mutation BackofficeDeleteExhibitor($exhibitorId: ID!) {
                        backofficeDeleteExhibitor(exhibitorId: $exhibitorId)
                    }
                `,
        variables: { exhibitorId },
        errorMessage: "Falha ao excluir expositor",
        useNewApi: true,
      });

      if (response.data.backofficeDeleteExhibitor) {
        setToast({
          type: "success",
          message: "Expositor excluído com sucesso",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        // Redirect back to event details
        push(`/events/${eventId}`);
      } else {
        setToast({
          type: "error",
          message: "Falha ao excluir expositor",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting exhibitor:", error);
      setToast({
        type: "error",
        message: "Erro ao excluir expositor. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } finally {
      closeAlert();
    }
  };

  if (loading) {
    return (
      <Container>
        <Card title="Carregando..." typography="subtitle-medium">
          <div>Carregando dados do expositor...</div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Card title="Informações Básicas" typography="subtitle-medium">
          <FormSection>
            <Wrapper>
              <ImagePreview
                imageUrl={imagePath ?? inputsValues.logo?.signedFileUrl ?? ""}
                width={156}
                height={156}
              />
              <ImageDetails>
                Sempre que possível utilize um arquivo de proporção 1:1
                <InputImage
                  error={touched.logo && !!errors.logo}
                  errorMessage="Nenhuma imagem encontrada"
                  errorByTypeImage={!logoIsPNG && !!imagePath}
                  errorMessageByTypeImage="Por favor, utilize arquivos em .PNG"
                  onChange={handleImageChange}
                />
              </ImageDetails>
            </Wrapper>
            <Wrapper>
              <InputFormField
                label="Nome *"
                name="name"
                value={inputsValues.name}
                onChange={handleInputChange}
                error={touched.name && !!errors.name}
                mensageError={getErrorMessage(errors.name)}
                block
              />
            </Wrapper>
            <Wrapper>
              <InputFormField
                label="Ordem de Exibição"
                name="displayPosition"
                type="number"
                value={inputsValues.displayPosition?.toString() || ""}
                onChange={handleInputChange}
                error={touched.displayPosition && !!errors.displayPosition}
                mensageError={getErrorMessage(errors.displayPosition)}
                block
              />
            </Wrapper>
            <Wrapper>
              <RichTextEditor
                value={inputsValues.description}
                onChange={(value: string) => {
                  const event = {
                    target: {
                      name: "description",
                      value: value,
                    },
                  } as ChangeEvent<HTMLInputElement>;
                  handleInputChange(event);
                }}
                label="Descrição"
                error={touched.description && !!errors.description}
                mensageError={getErrorMessage(errors.description)}
                block
              />
            </Wrapper>
            <Wrapper>
              <CheckBox
                value={inputsValues.isSponsor}
                label="É Patrocinador"
                name="isSponsor"
                checked={inputsValues.isSponsor}
                onChange={({ checked }) => {
                  handleCheckboxChange({
                    target: { name: "isSponsor", checked },
                  } as any);
                }}
              />
            </Wrapper>
          </FormSection>
        </Card>

        {inputsValues.isSponsor && (
          <Card title="Informações de Patrocínio" typography="subtitle-medium">
            {inputsValues.isSponsor && (
              <div>
                <Wrapper>
                  <div style={{ width: "100%", height: "50%" }}>
                    <SelectModified
                      label="Nível de Patrocínio"
                      name="sponsorTier"
                      value={inputsValues.sponsorTier}
                      onChange={handleSelectChange}
                      options={sponsorTierOptions}
                      error={touched.sponsorTier && !!errors.sponsorTier}
                      errorMessage={getErrorMessage(errors.sponsorTier)}
                      block
                    />
                  </div>

                  <InputFormField
                    label="Número do Estande"
                    name="boothNumber"
                    value={inputsValues.boothNumber}
                    onChange={handleInputChange}
                    error={touched.boothNumber && !!errors.boothNumber}
                    mensageError={getErrorMessage(errors.boothNumber)}
                    block
                  />
                </Wrapper>

                <InputFormField
                  label="Website"
                  name="websiteUrl"
                  value={inputsValues.websiteUrl}
                  onChange={handleInputChange}
                  error={touched.websiteUrl && !!errors.websiteUrl}
                  mensageError={getErrorMessage(errors.websiteUrl)}
                  placeholder="https://exemplo.com"
                  block
                />
              </div>
            )}
          </Card>
        )}

        <Card title="Informações de Contato" typography="subtitle-medium">
          <Wrapper>
            <InputFormField
              label="E-mail"
              name="contactEmail"
              type="email"
              value={inputsValues.contactEmail}
              onChange={handleInputChange}
              error={touched.contactEmail && !!errors.contactEmail}
              mensageError={getErrorMessage(errors.contactEmail)}
              block
            />

            <InputFormField
              label="Telefone"
              name="contactPhone"
              value={inputsValues.contactPhone}
              onChange={handleInputChange}
              error={touched.contactPhone && !!errors.contactPhone}
              mensageError={getErrorMessage(errors.contactPhone)}
              block
            />
          </Wrapper>
        </Card>

        <Card title="Arquivos" typography="subtitle-medium">
          <InputMultipleFiles
            files={uploadedFiles}
            onAddFiles={handleAddFiles}
            onRemoveFile={handleRemoveFile}
            maxFiles={10}
            accept="*/*"
          />
        </Card>

        <FormActions>
          {isEditing ? (
            <Button
              label="Excluir Expositor"
              typeBtn="danger"
              icon={iconWhiteTrash}
              onClick={handleDeleteExhibitor}
              type="button"
            />
          ) : (
            <div />
          )}
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              label="Cancelar"
              typeBtn="outline"
              onClick={() => push(`/events/${eventId}?tab=Expositores`)}
              type="button"
            />
            <Button
              label={isEditing ? "Salvar Alterações" : "Criar Expositor"}
              typeBtn="blue"
              type="submit"
              load={isLoading}
              disabled={isLoading}
            />
          </div>
        </FormActions>
      </form>
    </Container>
  );
};
